import React from "react";
import Select from "react-select";
import { debounce } from "es-toolkit";
import LineOrderList from "../lineOrderList/LineOrderList";
import Loader from "@/modules/shared/ui/Loader";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { shiftNumber, clamp } from "@/lib/util";
import { subscribe, unsubscribe, publishSync, MSG_TYPES } from "@/lib/pubSub";
import local_settings from "@/lib/localSettings";
import UnscheduledOrders from "../core/UnscheduledOrders";
import ResizableHandle from "@/modules/pg/shared/planningTable/interaction/ResizableHandle";
import { OPERATION_SORT_BY, OPERATION_SORT_DIRECTION } from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import { FIELD_ORDERS_HEIGHT_RATIO } from "@/lib/localSettings";
import { LinesOrdersLogic } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";

import type {
    LineOrders,
    OperationSortBy,
    OperationSortDirection,
} from "@/modules/pg/shared/planningTable/reducers/linesOrders";
import type { PlanningTableColumn } from "@/modules/pg/shared/viewsCommon";

export const default_configuration = (() => {
    const current_date = new Date();
    const shift_number = shiftNumber(current_date);

    return {
        week: shift_number.week,
        year: shift_number.year,
        shift: shift_number.shift,
    };
})();

export const OrdersTabHeader = () => {
    const title = translate("OrderTableProduction.operations", "Operations");
    return (
        <span title={title}>
            <i className="fas fa-file-alt" />
            <span>{title}</span>
        </span>
    );
};

const getSelectTheme = (theme: any) => {
    return {
        ...theme,
        spacing: {
            ...theme.spacing,
            baseUnit: 2,
            controlHeight: 30,
        },
    };
};

type SortByOption = {
    label: string;
    value: OperationSortBy;
};

const getOrderByOptions = (): SortByOption[] => {
    return [
        {
            label: translate("common.earliest_start", "Earliest start"),
            value: OPERATION_SORT_BY.earliest_start,
        },
        {
            label: translate("common.earliest_end", "Earliest end"),
            value: OPERATION_SORT_BY.earliest_end,
        },
        {
            label: translate("common.deadline", "Deadline"),
            value: OPERATION_SORT_BY.deadline,
        },
    ];
};

type SortDirectionOption = {
    label: string;
    value: OperationSortDirection;
};

const getSortDirectionOptions = (): SortDirectionOption[] => {
    return [
        {
            label: translate("common.ascending", "Ascending"),
            value: OPERATION_SORT_DIRECTION.ascending,
        },
        {
            label: translate("common.descending", "Descending"),
            value: OPERATION_SORT_DIRECTION.descending,
        },
    ];
};

type OrderTabContentProps = {
    show_planning_details_filter: boolean;
    show_unscheduled_orders: boolean;
    line_group_uuid: string;
    plant_uuid: string;
    lines_orders_loading: boolean;
    lines_orders: LineOrders[];
    unscheduled_sort_by: OperationSortBy;
    unscheduled_sort_direction: OperationSortDirection;
    columns: PlanningTableColumn[];
    unscheduled_columns: PlanningTableColumn[];
    onClickInfo: (title: string) => (e: React.MouseEvent<HTMLElement>) => void;
    onSortChange: (sort_by: OperationSortBy, sort_direction: OperationSortDirection) => void;
    campaign_reference_key: string | null;
    highlighted_move_order_uuid: string | null;
};
type OrderTabContentState = {
    orders_height_ratio: number;
};

export default class OrdersTabContent extends React.Component<OrderTabContentProps, OrderTabContentState> {
    static SCROLL_SOURCE = "planning-table";
    static DEFAULT_ORDERS_HEIGHT = 0.5;

    container_ref = React.createRef<HTMLDivElement>();
    scroll_node_ref = React.createRef<HTMLDivElement>();
    safe_to_scroll_ref: React.MutableRefObject<boolean> = { current: true };

    scroll_subscription!: ReturnType<typeof subscribe>;
    scroll_end_subscription!: ReturnType<typeof subscribe>;

    state: OrderTabContentState = {
        // If values fall outside typical bounds, revert to default to maintain a user-friendly UI
        orders_height_ratio:
            local_settings.getNumberIfInRange(FIELD_ORDERS_HEIGHT_RATIO, 0.05, 0.95) ??
            OrdersTabContent.DEFAULT_ORDERS_HEIGHT,
    };

    componentDidMount() {
        this.safe_to_scroll_ref.current = true;

        this.scroll_subscription = subscribe(
            MSG_TYPES.planning_table_align_scroll,
            (data: { top?: number; left?: number; source?: string; enforce?: boolean }) => {
                if (data.source !== OrdersTabContent.SCROLL_SOURCE) {
                    this.safe_to_scroll_ref.current = false;
                    this.updateScroll(...[data.top, data.left]);
                }
            }
        );
        this.scroll_end_subscription = subscribe(MSG_TYPES.planning_table_align_scroll_end, () => {
            this.safe_to_scroll_ref.current = true;
        });
    }

    componentWillUnmount() {
        unsubscribe(this.scroll_subscription);
        unsubscribe(this.scroll_end_subscription);
    }

    handleSortByChange = (option?: SortByOption | null) => {
        if (option) {
            this.props.onSortChange(option.value, this.props.unscheduled_sort_direction);
        }
    };

    handleSortDirectionChange = (option?: SortDirectionOption | null) => {
        if (option) {
            this.props.onSortChange(this.props.unscheduled_sort_by, option.value);
        }
    };

    handleResize = (movement: number) => {
        if (this.container_ref && this.container_ref.current) {
            const rect = this.container_ref.current.getBoundingClientRect();
            const min = ResizableHandle.WIDTH;
            const max = rect.height - ResizableHandle.WIDTH;
            const absolute_height = clamp(
                Math.round(this.state.orders_height_ratio * rect.height) + movement,
                min,
                max
            );

            if (rect.height !== 0) {
                this.setState({ orders_height_ratio: clamp(absolute_height / rect.height, 0, 1) });
            } else {
                console.error("Can't divide by zero!");
            }
        }
    };

    handleResizeStop = () => {
        local_settings.setItem(FIELD_ORDERS_HEIGHT_RATIO, this.state.orders_height_ratio.toString());
    };

    handleScroll = (event: React.SyntheticEvent<HTMLDivElement>) => {
        if (this.safe_to_scroll_ref.current) {
            publishSync(MSG_TYPES.planning_table_align_scroll, {
                top: event.currentTarget.scrollTop,
                source: OrdersTabContent.SCROLL_SOURCE,
            });
        }
    };

    handleScrollEnd = debounce(() => {
        publishSync(MSG_TYPES.planning_table_align_scroll_end);
    }, 100);

    updateScroll(top?: number, left?: number) {
        if (this.scroll_node_ref.current) {
            if (top !== undefined) {
                this.scroll_node_ref.current.scrollTop = top;
            }
            if (left !== undefined) {
                this.scroll_node_ref.current.scrollLeft = left;
            }
        }
    }

    render() {
        // still loading
        if (this.props.lines_orders_loading) {
            return <Loader small={true} message={translate("common.preparing_operations_table")} />;
        }

        for (const line of this.props.lines_orders) {
            line.orders = LinesOrdersLogic.addCampaignGroups(
                line.orders,
                this.props.highlighted_move_order_uuid && this.props.campaign_reference_key
            );
        }

        const lines_components = this.props.lines_orders.map((line: LineOrders, i) => {
            return (
                <div key={"line" + i + line.line_uuid}>
                    <LineOrderList
                        columns={this.props.columns}
                        line_uuid={line.line_uuid}
                        line_group_uuid={this.props.line_group_uuid}
                        compact={true}
                        week={default_configuration.week}
                        plant_uuid={this.props.plant_uuid}
                        year={default_configuration.year}
                        onClickInfo={this.props.onClickInfo}
                    />
                </div>
            );
        });

        const { orders_height_ratio } = this.state;

        const sort_by_options = getOrderByOptions();
        const selected_sort_by_option = sort_by_options.find(option => option.value === this.props.unscheduled_sort_by);
        const sort_direction_options = getSortDirectionOptions();
        const selected_sort_direction_option = sort_direction_options.find(
            option => option.value === this.props.unscheduled_sort_direction
        );

        return (
            <div
                ref={this.container_ref}
                id="planning-details-orders"
                className="d-flex flex-column flex-grow-1 overflow-hidden"
            >
                <div
                    id="scheduled-orders"
                    className="overflow-auto"
                    style={{
                        height: this.props.show_unscheduled_orders ? `${orders_height_ratio * 100}%` : "100%",
                    }}
                    ref={this.scroll_node_ref}
                    onScroll={e => {
                        this.handleScroll(e);
                        this.handleScrollEnd();
                    }}
                >
                    {lines_components}
                </div>

                {this.props.show_unscheduled_orders && (
                    <>
                        <ResizableHandle
                            position={`${orders_height_ratio * 100}%`}
                            onMove={this.handleResize}
                            onMoveStop={this.handleResizeStop}
                        />
                        <div
                            id="unscheduled-orders"
                            className="overflow-auto_ d-flex flex-column flex-grow-1"
                            style={{
                                height: orders_height_ratio > 0 ? `${1 - orders_height_ratio}%` : "50%",
                            }}
                        >
                            <div className="unscheduled-orders__toolbar">
                                <label className="unscheduled-orders__toolbar-label">
                                    <span>{translate("common.sort_by", "Sort by")}</span>
                                    <Select
                                        className="unscheduled-orders__toolbar-sort-by-select"
                                        value={selected_sort_by_option}
                                        options={sort_by_options}
                                        onChange={this.handleSortByChange}
                                        theme={getSelectTheme}
                                        components={{
                                            IndicatorSeparator: null,
                                        }}
                                    />
                                </label>
                                <Select
                                    className="unscheduled-orders__toolbar-sort-direction-select"
                                    value={selected_sort_direction_option}
                                    options={sort_direction_options}
                                    onChange={this.handleSortDirectionChange}
                                    theme={getSelectTheme}
                                    components={{
                                        IndicatorSeparator: null,
                                    }}
                                />
                            </div>
                            <div className="overflow-auto flex-grow-1">
                                <UnscheduledOrders columns={this.props.unscheduled_columns} />
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }
}
